import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete'
import { MatInput, MatInputModule } from '@angular/material/input'
import { Observable, debounceTime, filter, map, startWith } from 'rxjs'

import { CommonModule } from '@angular/common'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { untilDestroyed } from '@app/shared/utils/utils'
import { SharedModule } from '@shared/shared.module'
import { sortBy } from 'lodash'

@Component({
  selector: 'scope-ui-autocomplete',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatSelectModule,
    SharedModule,
  ],
  templateUrl: './scope-ui-autocomplete.component.html',
  styleUrls: ['./scope-ui-autocomplete.component.scss'],
})
export class ScopeUiAutocompleteComponent implements OnInit, OnChanges {
  private readonly destroy$

  @Input() label!: string

  _options!: any[]

  @Input() set options(value: any[]) {
    this._options = value
  }

  @Input() filterByAction?: boolean

  @Input() multiselect?: boolean

  @Input() isCheckboxMultiselect?: boolean
  @Input() showSelectedOptionsAmount?: boolean = this.isCheckboxMultiselect

  @Input() componentId!: string

  @Input() hasIcon = false

  @Input() optionClass?: string

  @Input() iconName!: string

  @Input() placeholder?: string

  @Input() markerProperty?: string

  @Input() markerText: string

  @Input() set initialValue(value: any) {
    this.selectedValue = value
    if (!this.ignoreNewValue) {
      this.control.setValue(value)
    }
    this.ignoreNewValue = false
    this.cdr.detectChanges()
  }

  @Input() orderBy!: string

  @Input() optionFn!: (value: any) => string

  @Input() displayFn!: (value: any) => string

  @Input() noResultsMessage?: string = 'No results found'

  @Input() set disabled(value: boolean) {
    if (value) this.control.disable()
    else this.control.enable()
  }

  @Input() required = false

  @Input() enableDefaultOption = false

  @Input() hideToggleIcon = false

  @Input() showBreadcrumbs = false

  @Input() control = new FormControl<string | any>('')

  @Output() onSearch!: EventEmitter<{ searchString: string; event?: MatAutocompleteSelectedEvent }>

  @Output() onSelectDefaultOption!: EventEmitter<string>

  @Output() onSelectionChange!: EventEmitter<{ event: any; componentId?: any }>

  @ViewChild(MatAutocompleteTrigger) matAutocompleteTrigger: MatAutocompleteTrigger
  @ViewChild(MatAutocomplete) matAutocomplete: MatAutocomplete
  @ViewChild(MatInput, { static: false }) input: MatInput

  selectedValue: any

  filteredOptions!: Observable<any[]>
  selectedItems: any[] = []
  selectedItemsControl = new FormControl<string | any>('')
  isFocused: boolean

  searchText: string

  ignoreNewValue = false

  constructor(private cdr: ChangeDetectorRef) {
    this.onSearch = new EventEmitter<any>()
    this.onSelectDefaultOption = new EventEmitter<string>()
    this.onSelectionChange = new EventEmitter<any>()
    this.destroy$ = untilDestroyed()
  }

  ngOnInit() {
    if (!this.displayFn) {
      this.displayFn = (item: any) => {
        return item && item.name ? item.name : ''
      }
    }
    this.cdr.detectChanges()
    if (this.filterByAction) {
      this.autoCompleteByAction()
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.filterByAction) {
      this.autoComplete()
    }
  }

  resetAutoComplete() {
    this.control.reset()
  }

  onInput() {
    if (this.selectedValue) {
      this.selectedValue = null
      if (!this.multiselect) {
        this.ignoreNewValue = true
        this.onSelectionChange.emit({ event: undefined, componentId: this.componentId })
      }
    }
  }

  runSearch = (value) => {
    this.searchText = value
    this.onSearch.emit({ searchString: this.searchText })
  }

  autoComplete() {
    this.filteredOptions = this.control.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value) => {
        this.runSearch(value)
        let options = this.searchText ? this._filter(this.searchText as string) : this._options?.slice()
        if (options?.length === 0) {
          options = [{ id: -1, displayName: 'No Results' }]
        }
        return options
      })
    )
  }

  autoCompleteByAction() {
    this.control.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        filter((value) => typeof value === 'string'),
        map(this.runSearch)
      )
      .pipe(this.destroy$())
      .subscribe()
  }

  onSelection(event: MatAutocompleteSelectedEvent) {
    this.selectedValue = event.option.value
    if (this.selectedValue.id === -1) {
      this.onSelectDefaultOption.emit(this.searchText)
      this.control.setValue(this.searchText)
      return
    }
    this.onSelectionChange.emit({ event: this.selectedValue, componentId: this.componentId })
    if (this.filterByAction) {
      this.onSearch.emit({ searchString: '', event })
    }
    if (this.multiselect) {
      this.control.setValue('')
    }
  }

  private _filter(name: string): any[] {
    const filterValue = name.toLowerCase()
    return this._options?.filter((option) => {
      let value = this.optionFn ? this.optionFn(option) : option.name
      return value.toLowerCase().includes(filterValue)
    })
  }

  isMarkerPropertyAvailable(option: any): boolean {
    if (!this.markerProperty) {
      return false
    }
    if (typeof option[this.markerProperty] === 'function') {
      return option[this.markerProperty]()
    } else {
      return option[this.markerProperty]
    }
  }

  getOptionView(option: any) {
    if (option.id == -1) {
      return `<span class="autocomplete-option">${this.noResultsMessage}</span>`
    }
    if (option.getHtml) {
      return option.getHtml(this.searchText)
    } else {
      let value = this.optionFn ? this.optionFn(option) : option.name
      if (this.searchText) {
        let search = new RegExp(this.searchText, 'gi')
        value = value.replace(search, '<b>$&</b>')
      }

      let marker =
        this.markerProperty && this.isMarkerPropertyAvailable(option)
          ? `<div class="option-marker"><span>${this.markerText}</span></div>`
          : ''

      if (this.showBreadcrumbs) {
        return `<div class="option-container">
                  <span class="autocomplete-option">${value}</span>
                  <span class='autocomplete-breadcrumb'>${option.getBreadcrumb?.() || ''}</span>
                </div>`
      }
      if (marker) {
        return `<span class="autocomplete-option">${value}</span>
              ${marker}`
      } else {
        return `<span class="autocomplete-option">${value}</span>`
      }
    }
  }

  clear($event?: MouseEvent) {
    $event?.stopPropagation()
    this.selectedValue = null
    this.control.setValue('')

    if ($event) {
      this.input.focus()
      if (this.isCheckboxMultiselect) this.input.value = ''
    }

    if (!this.multiselect && !this.isCheckboxMultiselect) {
      this.onSelectionChange.emit({ event: undefined, componentId: this.componentId })
    }
  }

  toggle($event: MouseEvent) {
    if (this.matAutocomplete.isOpen) {
      $event.preventDefault()
      $event.stopPropagation()
      this.matAutocompleteTrigger.closePanel()
    }
  }

  getSelectedItemNames() {
    return sortBy(this.selectedItems, 'name')
      ?.map((item) => item.name)
      ?.join(', ')
  }

  toggleSelection(option: any) {
    if (option?.id) {
      if (this.isSelected(option)) this.selectedItems.splice(this.getItemIndex(option), 1)
      else this.selectedItems.push(option)
      this.onSelectionChange.emit({ event: this.selectedItems })
      this.onFocusOut(false)
      if (!this.searchText?.length) this.input.value = this.getSelectedItemNames()
    }
  }

  onFocusOut(resetSearch: boolean = true, $event?: FocusEvent) {
    if (this.isCheckboxMultiselect) {
      $event?.preventDefault()
      $event?.stopPropagation()
      this.isFocused = false

      if (this.searchText?.length && !resetSearch) this.input.value = this.searchText

      if (resetSearch) {
        this.input.value = this.getSelectedItemNames()
        this.clear()
      }
    }
  }

  isSelected = (option: any) => this.getItemIndex(option) !== -1
  getItemIndex = (option: any) => this.selectedItems.findIndex((item) => item === option)
}
